import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu as MenuIcon, X } from "lucide-react";
import { useSite } from "../../lib/site";
import { menuHref, href, switchLangPath } from "../../lib/i18n";
import { SmartLink } from "./Common";
import { img } from "../../lib/api";
import { StyledText } from "./StyledText";

function LangSwitch() {
  const { lang, site } = useSite();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const langs = (site.language?.active || ["tr"]).filter((l) => ["tr", "en"].includes(l));
  if (langs.length < 2) return null;
  return (
    <div className="flex items-center rounded-full border border-site-border p-0.5 text-xs font-semibold" data-testid="nav-lang-switcher">
      {langs.map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => l !== lang && nav(switchLangPath(pathname, lang, l) + search)}
          className={`px-2.5 py-1 rounded-full uppercase transition-colors ${l === lang ? "bg-s-primary text-s-onprimary" : "text-s-muted hover:text-s-text"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function DesktopItem({ m }) {
  const { lang, t } = useSite();
  const to = menuHref(m, lang);
  const cls = "nav-link px-3 py-2 text-[0.92rem] font-medium text-s-text/90 hover:text-s-primary transition-colors flex items-center gap-1";
  if (!m.children?.length) return <SmartLink to={to} newTab={m.new_tab} className={cls} data-testid={`nav-item-${m.id}`}>{t(m.label)}</SmartLink>;
  return (
    <div className="relative group" data-testid="nav-dropdown-menu">
      <SmartLink to={to} newTab={m.new_tab} className={cls} data-testid={`nav-item-${m.id}`}>
        {t(m.label)} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
      </SmartLink>
      <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-[opacity,transform] duration-200 z-50">
        <div className="min-w-[220px] rounded-xl border border-site-border bg-s-surface shadow-xl p-2">
          {m.children.map((c) => (
            <SmartLink key={c.id} to={menuHref(c, lang)} newTab={c.new_tab} data-testid={`nav-subitem-${c.id}`}
              className="block px-3 py-2 rounded-lg text-sm hover:bg-s-bg hover:text-s-primary transition-colors">{t(c.label)}</SmartLink>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileMenu({ onClose }) {
  const { site, lang, t, ui } = useSite();
  return (
    <div className="fixed inset-0 z-[80] bg-s-bg overflow-y-auto" data-testid="mobile-menu">
      <div className="flex justify-between items-center px-6 h-20 border-b border-site-border">
        <StyledText className="font-signature text-3xl text-s-primary" style={site.general?.site_name_style} text={t(site.general?.site_name) || site.profile?.name} testid="mobile-site-name" />
        <button onClick={onClose} aria-label="close" data-testid="mobile-menu-close"><X /></button>
      </div>
      <nav className="px-6 py-6 space-y-1">
        {site.menu.map((m) => (
          <div key={m.id}>
            <SmartLink to={menuHref(m, lang)} newTab={m.new_tab} onClick={onClose} className="block py-3 text-2xl font-heading">{t(m.label)}</SmartLink>
            {m.children?.map((c) => (
              <SmartLink key={c.id} to={menuHref(c, lang)} newTab={c.new_tab} onClick={onClose} className="block py-2 pl-4 text-s-muted">{t(c.label)}</SmartLink>
            ))}
          </div>
        ))}
        <div className="pt-6"><LangSwitch /></div>
        <Link to={href(lang, "contact")} onClick={onClose} className="mt-6 inline-flex rounded-[var(--s-radius)] bg-s-primary text-s-onprimary px-6 py-3 font-semibold">{ui.contact}</Link>
      </nav>
    </div>
  );
}

export default function Header() {
  const { site, lang, ui, t } = useSite();
  const [open, setOpen] = useState(false);
  const campaign = site.theme?.mode === "adaylik";
  return (
    <>
    <header data-testid="nav-header" className="sticky top-0 z-50 border-b border-site-border backdrop-blur-xl"
      style={{ background: "color-mix(in srgb, var(--s-bg) 82%, transparent)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-6">
        <Link to={href(lang, "home")} data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          {site.general?.logo ? <img src={img(site.general.logo)} alt={site.profile?.name} decoding="async" fetchpriority="high" className="h-10 w-auto" /> :
            <StyledText className="font-signature text-3xl sm:text-4xl leading-none text-s-primary" style={site.general?.site_name_style} text={t(site.general?.site_name) || site.profile?.name} testid="nav-site-name" />}
        </Link>
        <nav className="hidden lg:flex items-center gap-1">{site.menu.map((m) => <DesktopItem key={m.id} m={m} />)}</nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LangSwitch /></div>
          <Link to={campaign && site.campaign?.volunteer_enabled ? `${href(lang, "home")}#gonullu` : href(lang, "contact")} data-testid="nav-cta-button"
            className="hidden sm:inline-flex rounded-[var(--s-radius)] bg-s-primary text-s-onprimary px-5 py-2.5 text-sm font-semibold hover:opacity-90 hover:-translate-y-0.5 transition-[opacity,transform]">
            {campaign && site.campaign?.volunteer_enabled ? ui.volunteer : ui.contact}
          </Link>
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} aria-label={ui.menu} data-testid="mobile-menu-open"><MenuIcon /></button>
        </div>
      </div>
    </header>
    {open && <MobileMenu onClose={() => setOpen(false)} />}
    </>
  );
}
